import React, { useState } from 'react';
import { Form, Button, Typography, Checkbox, Card, Space, Divider, Grid, Spin } from 'antd';
import { FileTextOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useSelfRegistration } from '../../hooks/useSelfRegistration';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { SelfRegistrationSessionDTO } from '@/shared/api/model';
import { getCompanyDisplayName, useCompany } from '@/hooks/company/use-company';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface TermsAcceptanceStepProps {
    session: SelfRegistrationSessionDTO;
    onComplete: (session: SelfRegistrationSessionDTO) => void;
    onError: (message: string) => void;
}

const TermsAcceptanceStep: React.FC<TermsAcceptanceStepProps> = ({
    session,
    onComplete,
    onError
}) => {
    const [form] = Form.useForm();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { data: companyData } = useCompany();
    const institutionName = getCompanyDisplayName(companyData);

    // Use the refactored self registration hook
    const {
        acceptTerms,
        isAcceptingTerms
    } = useSelfRegistration();

    const [isNavigating, setIsNavigating] = useState(false);

    const handleAcceptTerms = async (values: any) => {
        console.log('🔍 handleAcceptTerms called with values:', values);
        
        if (!values.termsAccepted) {
            onError('You must accept the terms and conditions to continue');
            return;
        }

        try {
            if (!session.sessionToken) {
                throw new Error('Session token is required');
            }

            const request = {
                sessionToken: session.sessionToken,
                termsAccepted: true
            };

            console.log('🚀 Calling acceptTerms API with request:', request);
            
            // Call the actual API using the refactored hook and await the response
            const updatedSession = await acceptTerms(request);
            
            console.log('✅ Terms accepted successfully, updated session:', updatedSession);
            showSuccessNotification('Terms and conditions accepted');

            // Show a brief loader before navigating to give background processes time to settle
            setIsNavigating(true);
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Call onComplete with the updated session to advance to next step
            onComplete(updatedSession);
            
        } catch (error: any) {
            console.error('❌ Error in handleAcceptTerms:', error);
            setIsNavigating(false);
            onError(error.message || 'Failed to accept terms and conditions');
        }
    };

    return (
        <div>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '30px' }}>
                <FileTextOutlined style={{ 
                    fontSize: isMobile ? '36px' : '48px', 
                    color: '#1890ff', 
                    marginBottom: isMobile ? '12px' : '16px' 
                }} />
                <Title level={isMobile ? 4 : 3}>Terms and Conditions</Title>
                <Text type="secondary" style={{ 
                    fontSize: isMobile ? '14px' : '16px',
                    display: 'block'
                }}>
                    Please review and accept our terms and conditions to continue
                </Text>
            </div>

            <Card
                style={{
                    marginBottom: isMobile ? '20px' : '30px',
                    maxHeight: isMobile ? '300px' : '400px',
                    overflowY: 'auto',
                    border: '1px solid #d9d9d9'
                }}
                bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
                size={isMobile ? 'small' : 'default'}
            >
                <Title level={4}>{institutionName} Terms and Conditions</Title>
                
                <Paragraph>
                    <Text strong>1. Acceptance of Terms</Text>
                    <br />
                    By registering for a {institutionName} account, you agree to be bound by these terms and conditions.
                </Paragraph>

                <Paragraph>
                    <Text strong>2. Eligibility</Text>
                    <br />
                    You must be at least 18 years old and legally capable of entering into contracts to use our services.
                    You must provide accurate and complete information during registration.
                </Paragraph>

                <Paragraph>
                    <Text strong>3. Account Security</Text>
                    <br />
                    You are responsible for maintaining the security of your account credentials.
                    You must notify us immediately of any unauthorized use of your account.
                </Paragraph>

                <Paragraph>
                    <Text strong>4. Financial Services</Text>
                    <br />
                    Our services include savings accounts, loans, and other financial products.
                    All financial transactions are subject to applicable fees and charges.
                    Application fees, where applicable, are non-refundable.
                    Interest rates and terms may vary based on your membership status and creditworthiness.
                </Paragraph>

                <Paragraph>
                    <Text strong>5. Privacy and Data Protection</Text>
                    <br />
                    We collect and process your personal data in accordance with our Privacy Policy.
                    Your information may be shared with authorized third parties for service delivery.
                    We implement appropriate security measures to protect your data.
                </Paragraph>

                <Paragraph>
                    <Text strong>6. Compliance and Regulations</Text>
                    <br />
                    All services are provided in compliance with applicable banking and financial regulations.
                    You agree to provide necessary documentation for KYC (Know Your Customer) requirements.
                    We reserve the right to suspend or terminate accounts that violate regulatory requirements.
                </Paragraph>

                <Paragraph>
                    <Text strong>7. Limitation of Liability</Text>
                    <br />
                    {institutionName}'s liability is limited to the extent permitted by law.
                    We are not responsible for losses resulting from unauthorized account access due to your negligence.
                </Paragraph>

                <Paragraph>
                    <Text strong>8. Modifications</Text>
                    <br />
                    We reserve the right to modify these terms at any time with appropriate notice.
                    Continued use of our services constitutes acceptance of modified terms.
                </Paragraph>

                <Divider />

                <Paragraph>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        Last updated: {new Date().toLocaleDateString()}
                    </Text>
                </Paragraph>
            </Card>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleAcceptTerms}
                size={isMobile ? 'middle' : 'large'}
            >
                <Form.Item
                    name="termsAccepted"
                    valuePropName="checked"
                    rules={[
                        {
                            validator: (_, value) =>
                                value ? Promise.resolve() : Promise.reject(new Error('You must accept the terms and conditions'))
                        }
                    ]}
                >
                    <Checkbox>
                        <Space direction={isMobile ? 'vertical' : 'horizontal'} size="small">
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            <Text style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                I have read and agree to the <Text strong>Terms and Conditions</Text>
                            </Text>
                        </Space>
                    </Checkbox>
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isAcceptingTerms || isNavigating}
                        disabled={isAcceptingTerms || isNavigating}
                        block
                        size={isMobile ? 'middle' : 'large'}
                        style={{ 
                            height: isMobile ? '44px' : '48px',
                            fontSize: isMobile ? '16px' : '18px'
                        }}
                        icon={isNavigating ? <LoadingOutlined /> : undefined}
                    >
                        {isNavigating ? 'Proceeding...' : 'Accept and Continue'}
                    </Button>
                </Form.Item>
            </Form>

            <div style={{ textAlign: 'center', marginTop: isMobile ? '15px' : '20px' }}>
                <Text type="secondary" style={{ 
                    fontSize: isMobile ? '11px' : '12px',
                    lineHeight: isMobile ? '1.4' : '1.2'
                }}>
                    By accepting these terms, you acknowledge that you have read and understood
                    all the conditions outlined above.
                </Text>
            </div>
        </div>
    );
};

export default TermsAcceptanceStep;
